import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';

const ASSET_LABELS = {
  blog: 'Blog', newsletter: 'Newsletter', email_sequence: 'Email Seq.',
  one_pager: 'One-Pager', banner: 'Banner', service_page: 'Svc Page', presentation: 'Presentation',
  marketing_strategy: 'Mktg Strategy',
};
const ASSET_TYPES = Object.keys(ASSET_LABELS);

export default function AdminLimits() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLimits, setEditingLimits] = useState({});
  const { addToast } = useToast();

  useEffect(() => {
    api.get('/admin/partners').then(async ({ data }) => {
      // Enrich with limits
      const enriched = await Promise.all(data.map(async (p) => {
        try {
          // We need to get limits from the partner's perspective
          // The admin route returns the partner with assetLimits if we include them
          return p;
        } catch {
          return p;
        }
      }));
      setPartners(enriched);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSaveLimits = async (partnerId) => {
    const limits = editingLimits[partnerId];
    if (!limits) return;
    try {
      const { data: saved } = await api.put(`/admin/partners/${partnerId}/limits`, { limits });
      // Show what was actually saved (the list was loaded before this edit).
      setPartners(prev => prev.map(p => (p.id === partnerId ? { ...p, assetLimits: saved } : p)));
      addToast('Limits updated');
      setEditingLimits(prev => { const n = {...prev}; delete n[partnerId]; return n; });
    } catch (err) {
      addToast('Failed to update limits', 'error');
    }
  };

  const updateLimit = (partnerId, assetType, value) => {
    setEditingLimits(prev => ({
      ...prev,
      [partnerId]: {
        ...(prev[partnerId] || {}),
        [assetType]: parseInt(value) || 0
      }
    }));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Asset Limits</h1>
      <p className="text-gray-500 mb-6">Set monthly generation limits per partner and asset type.</p>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">Partner</th>
              {ASSET_TYPES.map(type => (
                <th key={type} className="text-center px-3 py-3 text-sm font-medium text-gray-600 whitespace-nowrap">{ASSET_LABELS[type]}</th>
              ))}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {partners.map(partner => {
              const limits = partner.assetLimits || [];
              const editing = editingLimits[partner.id] || {};
              return (
                <tr key={partner.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">{partner.companyName}</td>
                  {ASSET_TYPES.map(type => {
                    const limit = limits.find(l => l.assetType === type);
                    // Show the saved value. No saved limit means 0: the partner can't
                    // generate this type (never display a default that isn't saved).
                    const currentVal = editing[type] !== undefined ? editing[type] : (limit ? limit.monthlyLimit : 0);
                    return (
                      <td key={type} className="px-3 py-3 text-center">
                        <input
                          type="number"
                          min="0"
                          value={currentVal}
                          onChange={e => updateLimit(partner.id, type, e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-v2-blue outline-none"
                        />
                        <div className="text-xs text-gray-400 mt-1">{limit ? `${limit.usedThisMonth} used` : 'not set'}</div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3">
                    {editingLimits[partner.id] && (
                      <button onClick={() => handleSaveLimits(partner.id)}
                        className="px-3 py-1 bg-v2-blue text-white rounded text-sm hover:bg-blue-700">
                        Save
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
